import React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Eye, EyeOff } from 'lucide-react';
import { useCompanyContacts } from '../../hooks/useCompanyContacts';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useAnonymization } from '../../utils/anonymize';
import LoadingSpinner from '../ui/LoadingSpinner';
import { Skeleton } from '../ui/Skeleton';
import { useLocation } from 'react-router-dom';

interface CompanyContactsProps {
  companyId: string;
}

export default function CompanyContacts({ companyId }: CompanyContactsProps) {
  const { contacts, loading, error } = useCompanyContacts(companyId);
  const { settings, loading: settingsLoading } = useUserSettings();
  const location = useLocation();
  const [temporaryReveal, setTemporaryReveal] = useState(false);
  
  const shouldAnonymize = settings?.anonymizing_mode && !temporaryReveal;

  if (loading) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Contacts</h2>
        <LoadingSpinner message="Loading contacts..." />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="card p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Contacts</h2>
        {settings?.anonymizing_mode && (
          <button
            onClick={() => setTemporaryReveal(!temporaryReveal)}
            className="btn btn-secondary flex items-center gap-2"
            title={temporaryReveal ? "Hide sensitive data" : "Reveal sensitive data"}
          >
            {temporaryReveal ? (
              <>
                <EyeOff className="w-4 h-4" />
                Hide
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Reveal
              </>
            )}
          </button>
        )}
      </div>
      
      {contacts.length === 0 ? (
        <p className="text-gray-400">No contacts found</p>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <Link
              key={contact.id}
              to={`/contacts/${contact.id}`}
              state={{ from: location.pathname }}
              className="flex items-start justify-between p-4 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
            >
              {settingsLoading ? (
                <div className="space-y-2 w-full">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-48" />
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">
                    {useAnonymization(contact.first_name, 'name', shouldAnonymize)}{' '}
                    {useAnonymization(contact.last_name, 'name', shouldAnonymize)}
                  </p>
                  <p className="text-sm text-primary-400">{contact.job_title}</p>
                  {contact.work_email && (
                    <div className="flex items-center text-sm text-gray-400">
                      <Mail className="w-4 h-4 mr-2" />
                      {useAnonymization(contact.work_email, 'email', shouldAnonymize)}
                    </div>
                  )}
                  {contact.work_phone && (
                    <div className="flex items-center text-sm text-gray-400">
                      <Phone className="w-4 h-4 mr-2" />
                      {shouldAnonymize ? '**********' : contact.work_phone}
                    </div>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}